'use client';

import { Globe } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { LocaleCode } from '@/i18n/locales';
import { getCountryName } from '@/shared/utils/get-country-list';
import { useCurrency } from '@/features/shared/hooks/use-currency';
import { useCountryISO } from '@/features/shared/hooks/use-country-iso';

/**
 * Read-only replacement for the header currency picker: shows which region the prices
 * on the page are for. Renders nothing until `RegionHydrator` has resolved the country,
 * so we never name a country we have not detected.
 */
export const FooterRegion = () => {
  const locale = useLocale() as LocaleCode;
  const countryISO = useCountryISO();
  const currency = useCurrency();
  const t = useTranslations('Region');

  if (!countryISO) return null;

  const countryName = getCountryName(locale, countryISO);
  if (!countryName) return null;

  return (
    <p
      className='text-text-muted flex items-center gap-2'
      aria-label={t('yourRegion', { country: countryName, currency })}
    >
      <Globe size={16} aria-hidden='true' />
      <span>{countryName}</span>
      <span aria-hidden='true'>·</span>
      <span>{currency}</span>
    </p>
  );
};
